import { useCallback, useMemo } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

export const useAifcAnalytics = (
	messageId: string,
): {
	sendAifcAnalyticsEvent: (
		payload: AnalyticsEventPayload | UIAnalyticsEvent,
		options?: { removeDefaultTags?: boolean },
	) => void;
} => {
	const { sendAnalyticsEvent } = useAnalytics();

	// conversationId is already sent as `conversationChannelId`, a default attribute in the instrumentation.
	const baseAttributes = useMemo(() => {
		return fg('jira-aifc-workflow-resumption-ui-updates-targeting')
			? {
					messageId,
					variant: FeatureGates.getExperimentValue(
						'jira-aifc-workflow-resumption-ui-updates',
						'cohort',
						'control',
						{ fireExperimentExposure: false },
					),
				}
			: {};
	}, [messageId]);

	const sendAifcAnalyticsEvent = useCallback(
		(
			payload: AnalyticsEventPayload | UIAnalyticsEvent,
			options?: { removeDefaultTags?: boolean },
		): void => {
			const attributes = 'attributes' in payload ? payload.attributes : {};
			sendAnalyticsEvent(
				{
					...payload,
					attributes: {
						...baseAttributes,
						...attributes,
					},
				},
				...(options ? [options] : []),
			);
		},
		[sendAnalyticsEvent, baseAttributes],
	);

	return {
		sendAifcAnalyticsEvent,
	};
};
